import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { useFormikContext } from "formik";
import debounce from "lodash/debounce";
import { Trans, useTranslation } from "react-i18next";
import { FILESYSTEM_TYPE, S3_TYPE, GCS_TYPE, ABS_TYPE } from "archive/ArchiveStore";
import { OVH_TYPE, SYSELEVEN_TYPE } from "archive/logic/s3CompatibleProviders";
import { FormikFormGroup } from "components/common";
const StyledDiv = styled.div`
  margin: 10px 0;
`;
const ArchiveBackendOutputPathFieldGroup = ({ checkOutputPath, noPreview = false }) => {
  const { t } = useTranslation("enterprise_archive");
  const { values: formValues, setFieldError } = useFormikContext();
  const [pathCheck, setPathCheck] = useState(null);
  const [pathError, setPathError] = useState(null);
  const _validateOutputPath = useCallback(
    (path) => {
      checkOutputPath(path).then((response) => {
        setPathCheck(response.result);
        setPathError(null);
      }).catch((error) => {
        try {
          setPathCheck(null);
          const errorMessage = error.additional.body.message;
          setPathError(errorMessage);
          setFieldError("settings.output_path", errorMessage);
        } catch {
          setPathCheck(null);
          setPathError(error.message);
          setFieldError("settings.output_path", error.message);
        }
      });
    },
    [checkOutputPath, setFieldError]
  );
  const _debouncedValidateOutputPath = debounce(_validateOutputPath, 500);
  const labelText = {
    [S3_TYPE]: t("archive_backend_output_path_field_group.label_s3"),
    [OVH_TYPE]: t("archive_backend_output_path_field_group.label_ovh"),
    [SYSELEVEN_TYPE]: t("archive_backend_output_path_field_group.label_syseleven"),
    [GCS_TYPE]: t("archive_backend_output_path_field_group.label_gcs"),
    [ABS_TYPE]: t("archive_backend_output_path_field_group.label_abs"),
    [FILESYSTEM_TYPE]: t("archive_backend_output_path_field_group.label_filesystem")
  };
  const exampleText = {
    [S3_TYPE]: t("archive_backend_output_path_field_group.example_s3"),
    [OVH_TYPE]: t("archive_backend_output_path_field_group.example_ovh"),
    [SYSELEVEN_TYPE]: t("archive_backend_output_path_field_group.example_syseleven"),
    [GCS_TYPE]: t("archive_backend_output_path_field_group.example_gcs"),
    [ABS_TYPE]: t("archive_backend_output_path_field_group.example_abs"),
    [FILESYSTEM_TYPE]: t("archive_backend_output_path_field_group.example_filesystem")
  };
  const placeholder = {
    [S3_TYPE]: "/data/archives/example",
    [OVH_TYPE]: "/data/archives/example",
    [SYSELEVEN_TYPE]: "/data/archives/example",
    [GCS_TYPE]: "data/archives/example",
    [ABS_TYPE]: "data/archives/example",
    [FILESYSTEM_TYPE]: "/data/archives/example"
  };
  const helpText = {
    [S3_TYPE]: t("archive_backend_output_path_field_group.help_s3"),
    [OVH_TYPE]: t("archive_backend_output_path_field_group.help_ovh"),
    [SYSELEVEN_TYPE]: t("archive_backend_output_path_field_group.help_syseleven"),
    [GCS_TYPE]: t("archive_backend_output_path_field_group.help_gcs"),
    [ABS_TYPE]: t("archive_backend_output_path_field_group.help_abs"),
    [FILESYSTEM_TYPE]: t("archive_backend_output_path_field_group.help_filesystem")
  };
  const getPathError = () => pathError && /* @__PURE__ */ React.createElement("span", null, /* @__PURE__ */ React.createElement("strong", null, t("archive_backend_output_path_field_group.error_label")), " ", pathError);
  const _onChange = (event) => {
    _debouncedValidateOutputPath(event.target.value);
  };
  useEffect(() => {
    _validateOutputPath(formValues.settings.output_path);
  }, [formValues, _validateOutputPath]);
  return /* @__PURE__ */ React.createElement(StyledDiv, null, /* @__PURE__ */ React.createElement(
    FormikFormGroup,
    {
      name: "settings.output_path",
      placeholder: placeholder[formValues.settings.type],
      label: /* @__PURE__ */ React.createElement("span", null, labelText[formValues.settings.type]),
      required: formValues.settings.type === FILESYSTEM_TYPE,
      help: /* @__PURE__ */ React.createElement("span", null, helpText[formValues.settings.type], " ", /* @__PURE__ */ React.createElement(
        Trans,
        {
          t,
          i18nKey: "archive_backend_output_path_field_group.help_suffix",
          components: { strong: /* @__PURE__ */ React.createElement("strong", null) }
        }
      )),
      labelClassName: "",
      error: getPathError(),
      wrapperClassName: "",
      onChange: _onChange,
      disabled: !!(formValues == null ? void 0 : formValues.id)
    }
  ), !noPreview && /* @__PURE__ */ React.createElement("span", null, /* @__PURE__ */ React.createElement("strong", null, exampleText[formValues.settings.type], ":"), /* @__PURE__ */ React.createElement("code", null, pathCheck)));
};
export default ArchiveBackendOutputPathFieldGroup;
